/* eslint-disable react-refresh/only-export-components */
/**
 * Font Settings Context
 * Manages font settings for document export with server-backed sync
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  FontSettings,
  FontSettingsContextValue,
  FontPreset,
  FontElementSettings,
} from '@/types';
import {
  DEFAULT_FONT_SETTINGS,
  FONT_CONSTRAINTS,
  FONT_SETTINGS_STORAGE_KEY,
  FONT_PRESETS_STORAGE_KEY,
  MAX_FONT_PRESETS,
  validateFontSettings,
  clampFontSettings,
} from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { userPreferencesApi } from '@/services/api';
import { useDocumentSettingsState } from '@/hooks/useDocumentSettingsState';
import { debugEvent } from '@/lib/debugApi';

interface FontSettingsProviderProps {
  children: React.ReactNode;
}

/**
 * Exported so the one-click format provider can supply its own values to the same editor
 * subtree (SPEC-ONECLICK-FORMAT-001 design.md §6). The hook below is unchanged.
 */
export const FontSettingsContext = createContext<FontSettingsContextValue | undefined>(
  undefined
);

/**
 * Load font settings from localStorage
 */
const loadFontSettings = (): FontSettings => {
  try {
    const stored = localStorage.getItem(FONT_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const validated = validateFontSettings(parsed?.settings ?? parsed);
      if (validated) {
        return clampFontSettings(validated);
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { ...DEFAULT_FONT_SETTINGS };
};

/**
 * Load updatedAt timestamp from localStorage
 */
const loadUpdatedAt = (): number => {
  try {
    const stored = localStorage.getItem(FONT_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.updatedAt ?? 0;
    }
  } catch {
    return 0;
  }
  return 0;
};

/**
 * Load font presets from localStorage
 */
const loadFontPresets = (): FontPreset[] => {
  try {
    const stored = localStorage.getItem(FONT_PRESETS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed as FontPreset[];
      }
    }
  } catch (err) {
    console.error('[FontSettingsContext] Failed to load presets:', err);
  }
  return [];
};

/**
 * Generate a unique ID for presets
 */
const generatePresetId = (): string => {
  return `font_preset_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const resolvePresetSource = (currentPresets: FontPreset[]): FontPreset[] => {
  if (currentPresets.length > 0) {
    return currentPresets;
  }

  return loadFontPresets().slice(0, MAX_FONT_PRESETS);
};

/**
 * Validate and clamp font size
 */
const validateFontSize = (value: number): number => {
  if (Number.isNaN(value)) {
    return DEFAULT_FONT_SETTINGS.passage.fontSize;
  }
  return Math.min(Math.max(value, FONT_CONSTRAINTS.SIZE_MIN), FONT_CONSTRAINTS.SIZE_MAX);
};

/**
 * Validate and clamp line spacing
 */
const validateLineSpacing = (value: number): number => {
  if (Number.isNaN(value)) {
    return DEFAULT_FONT_SETTINGS.passage.lineSpacing ?? 1.2;
  }
  return Math.min(
    Math.max(value, FONT_CONSTRAINTS.LINE_SPACING_MIN),
    FONT_CONSTRAINTS.LINE_SPACING_MAX
  );
};

const validatePresets = (data: unknown): FontPreset[] => {
  if (!Array.isArray(data)) return [];
  const seen = new Set<string>();
  return data.flatMap((preset) => {
    if (!preset || typeof preset.id !== 'string' || !preset.id || seen.has(preset.id) ||
        typeof preset.name !== 'string' || !preset.name.trim()) return [];
    const settings = validateFontSettings(preset.settings);
    if (!settings) return [];
    seen.add(preset.id);
    return [{ id: preset.id, name: preset.name.trim().slice(0, 100),
      settings: clampFontSettings(settings), createdAt: Number.isFinite(preset.createdAt) ? preset.createdAt : 0 }];
  }).slice(0, MAX_FONT_PRESETS);
};

export function FontSettingsProvider({ children }: FontSettingsProviderProps) {
  const { user, isImpersonating } = useAuth();
  const canPersist = !!user && !isImpersonating;

  const [settings, setSettingsState, settingsRef] = useDocumentSettingsState<FontSettings>({ ...DEFAULT_FONT_SETTINGS });
  const [updatedAt, setUpdatedAt] = useState<number>(0);
  const [presets, setPresetsState, presetsRef] = useDocumentSettingsState<FontPreset[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isHydrating, setIsHydrating] = useState(!!user);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const scopeRef = useRef(0);
  const loadFailedRef = useRef(false);


  // The API owns the debounce so queued saves survive provider unmounts.
  const syncVersionRef = useRef(0);
  const isHydratingRef = useRef(false);

  // Queue immediately; userPreferencesApi merges rapid changes into one save.
  const syncToServer = useCallback(
    async (newSettings: FontSettings, newPresets: FontPreset[]) => {
      if (!canPersist || isHydratingRef.current) return;

      const scope = scopeRef.current;
      const syncVersion = ++syncVersionRef.current;
      setSyncError(null);
      setIsSyncing(true);
      try {
        debugEvent('FontSettings.sync:commit');
        const response = await userPreferencesApi.update({
          fontSettings: newSettings,
          fontPresets: newPresets,
        });

        if (scope !== scopeRef.current || syncVersion !== syncVersionRef.current) return;
        if (response?.fontSettingsUpdatedAt) {
          setUpdatedAt(response.fontSettingsUpdatedAt);
        }
        debugEvent('FontSettings.sync:success', {
          updatedAt: response?.fontSettingsUpdatedAt,
        });
      } catch (err) {
        console.error('[FontSettingsContext] Server sync failed:', err);
        debugEvent('FontSettings.sync:error', {
          error: err instanceof Error ? err.message : String(err),
        });
        if (scope === scopeRef.current && syncVersion === syncVersionRef.current) setSyncError('변경 사항을 저장하지 못했습니다. 다시 저장해 주세요.');
      } finally {
        if (scope === scopeRef.current && syncVersion === syncVersionRef.current) {
          setIsSyncing(false);
        }
      }
    },
    [canPersist]
  );

  // Hydrate from server on login
  useEffect(() => {
    const scope = ++scopeRef.current;
    let cancelled = false;
    setSettingsState({ ...DEFAULT_FONT_SETTINGS });
    setPresetsState([]);
    setUpdatedAt(0);
    setSyncError(null);
    setLoadFailed(false);
    loadFailedRef.current = false;
    setIsSyncing(false);
    isHydratingRef.current = !!user;
    setIsHydrating(!!user);
    if (!user) return;

    const hydrateFromServer = async () => {
      setIsHydrating(true);
      isHydratingRef.current = true;

      try {
        const legacySettings = loadFontSettings();
        const legacyUpdatedAt = loadUpdatedAt();
        const legacyPresets = validatePresets(loadFontPresets());
        debugEvent('FontSettings.hydrate:start', { updatedAt });
        const serverPrefs = await userPreferencesApi.get();
        if (cancelled || scope !== scopeRef.current) return;

        if (serverPrefs) {
          const serverSettings = serverPrefs.fontSettings as FontSettings | undefined;
          const serverPresets = validatePresets(serverPrefs.fontPresets);
          const serverUpdatedAt = (serverPrefs.fontSettingsUpdatedAt as number) ?? 0;

          if (serverSettings) {
            const validated = validateFontSettings(serverSettings);
            if (validated) {
              setSettingsState(clampFontSettings(validated));
              setUpdatedAt(serverUpdatedAt);
              debugEvent('FontSettings.hydrate:use-server', {
                serverUpdatedAt,
                localUpdatedAt: legacyUpdatedAt,
              });
            }
          }

          setPresetsState(serverPresets);

          if (
            !isImpersonating &&
            !serverSettings &&
            (legacyUpdatedAt > 0 || legacyPresets.length > 0)
          ) {
            debugEvent('FontSettings.hydrate:push-local', {
              localUpdatedAt: legacyUpdatedAt,
            });
            setSettingsState(clampFontSettings(legacySettings));
            setUpdatedAt(legacyUpdatedAt);
            const migratedPresets = serverPresets.length > 0 ? serverPresets : legacyPresets.slice(0, MAX_FONT_PRESETS);
            setPresetsState(migratedPresets);
            await userPreferencesApi.update({
              fontSettings: clampFontSettings(legacySettings),
              fontPresets: migratedPresets,
            });
          }

          if (cancelled || scope !== scopeRef.current) return;

          if (
            !isImpersonating &&
            (serverSettings || serverPresets.length > 0 || legacyUpdatedAt > 0 || legacyPresets.length > 0)
          ) {
            localStorage.removeItem(FONT_SETTINGS_STORAGE_KEY);
            localStorage.removeItem(FONT_PRESETS_STORAGE_KEY);
          }
        }
      } catch {
        if (cancelled) return;
        setLoadFailed(true);
        loadFailedRef.current = true;
        setSyncError('설정을 불러오지 못했습니다. 다시 불러와 주세요.');
        // Keep local values on failure
        debugEvent('FontSettings.hydrate:error');
      } finally {
        if (!cancelled) {
          setIsHydrating(false);
          // A failed load must not let defaults overwrite the saved preferences.
          isHydratingRef.current = false;
          debugEvent('FontSettings.hydrate:done');
        }
      }
    };

    void hydrateFromServer();
    return () => { cancelled = true; scopeRef.current = scope + 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isImpersonating, reloadVersion]);

  const setSettings = useCallback(
    (newSettings: Partial<FontSettings>) => {
      if (isHydratingRef.current || loadFailedRef.current) return;

      setSettingsState((prev) => {
        const updated = { ...prev };

        if (newSettings.title) {
          updated.title = {
            ...prev.title,
            ...newSettings.title,
            fontSize: validateFontSize(newSettings.title.fontSize ?? prev.title.fontSize),
          };
        }
        if (newSettings.passageNumber) {
          updated.passageNumber = {
            ...prev.passageNumber,
            ...newSettings.passageNumber,
            fontSize: validateFontSize(
              newSettings.passageNumber.fontSize ?? prev.passageNumber.fontSize
            ),
          };
        }
        if (newSettings.passage) {
          updated.passage = {
            ...prev.passage,
            ...newSettings.passage,
            fontSize: validateFontSize(newSettings.passage.fontSize ?? prev.passage.fontSize),
            lineSpacing: newSettings.passage.lineSpacing ?? prev.passage.lineSpacing ?? 1.2,
          };
        }
        if (newSettings.explanation) {
          updated.explanation = {
            ...prev.explanation,
            ...newSettings.explanation,
            fontSize: validateFontSize(
              newSettings.explanation.fontSize ?? prev.explanation.fontSize
            ),
            lineSpacing: newSettings.explanation.lineSpacing ?? prev.explanation.lineSpacing ?? 1.1,
          };
        }

        // Trigger server sync
        const validated = clampFontSettings(updated);
        syncToServer(validated, presetsRef.current);

        return validated;
      });

      // Update local timestamp (will be replaced by server response)
      setUpdatedAt(Date.now());
    },
    [presetsRef, setSettingsState, syncToServer]
  );

  const updateElement = useCallback(
    (element: keyof FontSettings, values: Partial<FontElementSettings>) => {
      if (isHydratingRef.current || loadFailedRef.current) return;

      setSettingsState((prev) => {
        const currentElement = prev[element];
        const updated = {
          ...prev,
          [element]: {
            ...currentElement,
            ...values,
            fontSize:
              values.fontSize !== undefined
                ? validateFontSize(values.fontSize)
                : currentElement.fontSize,
            lineSpacing:
              values.lineSpacing !== undefined
                ? values.lineSpacing
                : currentElement.lineSpacing,
          },
        };

        // Trigger server sync
        const validated = clampFontSettings(updated);
        syncToServer(validated, presetsRef.current);

        return validated;
      });

      setUpdatedAt(Date.now());
    },
    [presetsRef, setSettingsState, syncToServer]
  );

  const resetSettings = useCallback(() => {
    if (isHydratingRef.current || loadFailedRef.current) return;

    setSettingsState({ ...DEFAULT_FONT_SETTINGS });
    setUpdatedAt(Date.now());
    syncToServer({ ...DEFAULT_FONT_SETTINGS }, presetsRef.current);
  }, [presetsRef, setSettingsState, syncToServer]);

  const savePreset = useCallback(
    (name: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      const trimmedName = name.trim().slice(0, 100);
      if (!trimmedName) return;

      const newPreset: FontPreset = {
        id: generatePresetId(),
        name: trimmedName,
        settings: { ...settingsRef.current },
        createdAt: Date.now(),
      };

      setPresetsState((prev) => {
        const basePresets = user ? prev : validatePresets(resolvePresetSource(prev));
        const updated = [newPreset, ...basePresets].slice(0, MAX_FONT_PRESETS);
        syncToServer(settingsRef.current, updated);
        return updated;
      });
    },
    [setPresetsState, settingsRef, syncToServer, user]
  );

  const loadPreset = useCallback(
    (id: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      const preset = (user ? presetsRef.current : validatePresets(resolvePresetSource(presetsRef.current))).find((p) => p.id === id);
      if (preset) {
        const validated = validateFontSettings(preset.settings);
        if (validated) {
          setSettingsState(clampFontSettings(validated));
          setUpdatedAt(Date.now());
          // Sync to server to get new updatedAt
          syncToServer(clampFontSettings(validated), presetsRef.current);
        }
      }
    },
    [presetsRef, setSettingsState, syncToServer, user]
  );

  const deletePreset = useCallback(
    (id: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      setPresetsState((prev) => {
        const basePresets = user ? prev : validatePresets(resolvePresetSource(prev));
        const updated = basePresets.filter((p) => p.id !== id);
        syncToServer(settingsRef.current, updated);
        return updated;
      });
    },
    [setPresetsState, settingsRef, syncToServer, user]
  );

  const renamePreset = useCallback(
    (id: string, newName: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      const trimmedName = newName.trim().slice(0, 100);
      if (!trimmedName) {
        setPresetsState((prev) => user ? prev : validatePresets(resolvePresetSource(prev)));
        return;
      }

      setPresetsState((prev) => {
        const basePresets = user ? prev : validatePresets(resolvePresetSource(prev));
        const updated = basePresets.map((p) =>
          p.id === id ? { ...p, name: trimmedName } : p
        );
        syncToServer(settingsRef.current, updated);
        return updated;
      });
    },
    [setPresetsState, settingsRef, syncToServer, user]
  );

  const retrySync = useCallback(() => {
    if (loadFailed) setReloadVersion((value) => value + 1);
    else void syncToServer(settingsRef.current, presetsRef.current);
  }, [loadFailed, presetsRef, settingsRef, syncToServer]);

  useEffect(() => {
    if (!isSyncing && (!syncError || loadFailed)) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isSyncing, syncError, loadFailed]);

  const contextValue = useMemo<FontSettingsContextValue>(
    () => ({
      settings,
      updatedAt,
      setSettings,
      updateElement,
      resetSettings,
      validateFontSize,
      validateLineSpacing,
      presets,
      savePreset,
      loadPreset,
      deletePreset,
      renamePreset,
      isSyncing,
      isHydrating,
      syncError,
      loadFailed,
      retrySync,
    }),
    [
      settings,
      updatedAt,
      setSettings,
      updateElement,
      resetSettings,
      presets,
      savePreset,
      loadPreset,
      deletePreset,
      renamePreset,
      isSyncing,
      isHydrating,
      syncError,
      loadFailed,
      retrySync,
    ]
  );

  return (
    <FontSettingsContext.Provider value={contextValue}>
      {children}
    </FontSettingsContext.Provider>
  );
}

export function useFontSettings(): FontSettingsContextValue {
  const ctx = useContext(FontSettingsContext);
  if (!ctx) {
    throw new Error(
      'useFontSettings must be used within a FontSettingsProvider'
    );
  }
  return ctx;
}
